import dayjs from 'dayjs';
import { isDeepEqual } from 'remeda';

import { DailyTaskFormValues } from './form';

import { DailyTaskType, SpecialTaskType, UserRewardType } from 'v2/constants';
import { FormReward } from 'v2/types';
import { RewardModel } from 'v2/types/RewardModel';
import { DailyTaskModel, DailyTaskValue } from 'v2/types/TaskModel';

/** Собирает плоские поля формы в объект value, заменяя 0 на undefined (как в старой логике). */
const buildTaskValue = (values: DailyTaskFormValues, withBonusBuy: boolean): DailyTaskValue => {
  const { betLimit, taskX, isBonusBuy, lowerLimitX, totalTaskX, lowerRangeX, upperRangeX, countTaskX } = values;

  return {
    betLimit: betLimit === 0 ? undefined : betLimit,
    taskX: taskX === 0 ? undefined : taskX,
    isBonusBuy: withBonusBuy && isBonusBuy ? isBonusBuy : undefined,
    lowerLimitX: lowerLimitX === 0 ? undefined : lowerLimitX,
    totalTaskX: totalTaskX === 0 ? undefined : totalTaskX,
    lowerRangeX: lowerRangeX === 0 ? undefined : lowerRangeX,
    upperRangeX: upperRangeX === 0 ? undefined : upperRangeX,
    countTaskX: countTaskX === 0 ? undefined : countTaskX,
  };
};

interface BuildRequestParams {
  values: DailyTaskFormValues;
  defaultRewardIds: string[];
  specialRewardIds: string[];
}

/** Формирует тело запроса create/update в зависимости от isSpecial (как в старом DailyTask onSubmit). */
export const buildDailyTaskRequest = ({ values, defaultRewardIds, specialRewardIds }: BuildRequestParams) => {
  const {
    allowedUserRoles,
    description,
    type,
    gameId,
    tournamentId,
    date,
    isSpecial,
    specialTaskType,
    completedCountLimit,
    tasksToUnlock,
    referralsToUnlock,
    passwordToUnlock,
    goldenTicketId,
    endDate,
    imageId,
  } = values;

  if (isSpecial) {
    return {
      allowedUserRoles,
      defaultRewardIds: [],
      description,
      value: buildTaskValue(values, true),
      type,
      gameId,
      tournamentId,
      date: date.format(),
      completedCountLimit,
      tasksToUnlock,
      specialRewardIds,
      isSpecial,
      specialTaskType,
      referralsToUnlock,
      passwordToUnlock,
      goldenTicketId: specialTaskType === SpecialTaskType.GOLDEN_TICKET ? goldenTicketId : undefined,
      imageId: imageId ?? undefined,
    };
  }

  return {
    allowedUserRoles,
    defaultRewardIds,
    description,
    value: buildTaskValue(values, false),
    type,
    gameId,
    tournamentId,
    date: date.format(),
    isSpecial,
    endDate: type === DailyTaskType.TOP_X ? endDate.format() : undefined,
    imageId: imageId ?? undefined,
  };
};

/**
 * Готовит payload наград для bulk-создания.
 * Для special-задач награды размножаются по count (как в старой логике),
 * для default-задач count игнорируется — создаётся по одной награде.
 */
export const getRewardsPayload = (rewards: FormReward[], isSpecial: boolean): Pick<RewardModel, 'type' | 'value'>[] => {
  if (!isSpecial) {
    return rewards.map(({ type, value }) => ({ type, value }));
  }

  return rewards.flatMap(({ type, value, count = 1 }) => Array(Number(count)).fill({ type, value }));
};

/**
 * Сравнивает два набора наград независимо от порядка элементов —
 * чтобы при неизменённых наградах не пересоздавать их зря.
 */
export const areRewardsEqual = (
  first: Pick<RewardModel, 'type' | 'value'>[],
  second: Pick<RewardModel, 'type' | 'value'>[]
): boolean => {
  if (first.length !== second.length) {
    return false;
  }

  const sortByKey = (rewards: Pick<RewardModel, 'type' | 'value'>[]) =>
    [...rewards].sort((a, b) => `${a.type}_${a.value}`.localeCompare(`${b.type}_${b.value}`));

  return isDeepEqual(sortByKey(first), sortByKey(second));
};

/** Группирует плоский список наград в уникальные с count (для отображения special-наград при редактировании). */
export const groupRewardsWithCount = (rewards: RewardModel[]): FormReward[] => {
  const grouped = new Map<string, FormReward>();

  rewards.forEach(({ type, value }) => {
    const key = `${type}_${value}`;
    const existing = grouped.get(key);

    grouped.set(key, { type, value, count: (existing?.count ?? 0) + 1 });
  });

  return [...grouped.values()];
};

/** Маппит модель + загруженные награды в значения формы редактирования. */
export const getEditFormInitialValues = (dailyTask: DailyTaskModel, rewards: RewardModel[]): DailyTaskFormValues => {
  const {
    description,
    isSpecial,
    allowedUserRoles,
    gameId,
    type,
    completedCountLimit,
    tasksToUnlock,
    value,
    date,
    tournamentId,
    specialTaskType,
    referralsToUnlock,
    passwordToUnlock,
    goldenTicketId,
    endDate,
    imageId,
  } = dailyTask;

  return {
    completedCountLimit: completedCountLimit ?? 0,
    tasksToUnlock: tasksToUnlock ?? 0,
    referralsToUnlock: referralsToUnlock ?? 0,
    passwordToUnlock: passwordToUnlock ?? '',
    goldenTicketId: goldenTicketId ?? '',
    specialTaskType: specialTaskType ?? SpecialTaskType.TASKS,
    allowedUserRoles,
    date: dayjs(date),
    gameId,
    tournamentId,
    description,
    // Для special награды показываем сгруппированными по count, для default — как есть.
    rewards: isSpecial
      ? groupRewardsWithCount(rewards)
      : rewards.map(({ type: rewardType, value: rewardValue }) => ({ type: rewardType, value: rewardValue })),
    type,
    isSpecial,
    taskX: value.taskX ?? 0,
    isBonusBuy: value.isBonusBuy ?? false,
    betLimit: value.betLimit ?? 0,
    lowerLimitX: value.lowerLimitX ?? 0,
    totalTaskX: value.totalTaskX ?? 0,
    lowerRangeX: value.lowerRangeX ?? 0,
    upperRangeX: value.upperRangeX ?? 0,
    countTaskX: value.countTaskX ?? 0,
    endDate: endDate ? dayjs(endDate) : dayjs().add(1, 'day'),
    imageId: imageId ?? null,
  };
};

export const DEFAULT_REWARD_STUB: FormReward = { type: UserRewardType.BALANCE, value: '', count: 1 };
